/* eslint-disable no-console */
import type { OperationDefinitionNode } from 'graphql';
import { print } from 'graphql';
import { isPlainObject } from 'lodash';
import type { FetchResult } from '@apollo/client';
import { ApolloError, ApolloLink, Observable } from '@apollo/client';
import { CONFIG } from '@frameio/config';
import flog from '@frameio/flog';
import { reportError, startEvent } from '@frameio/telemetry';

export const TelemetryLink = () => {
  return new ApolloLink((operation, forward) => {
    const definition = operation.query.definitions.find(
      (q) => q.kind === 'OperationDefinition'
    ) as OperationDefinitionNode;

    const operationName = definition?.name?.value || 'unknown_operation';
    const operationType = definition?.operation;

    const query = definition.loc?.source?.body ?? print(definition);
    const namePrefix =
      query && query.trim().startsWith('subscription')
        ? `subscription.graphql`
        : `graphql`;
    const eventName = `${namePrefix}.${operationName}`;
    const transaction = startEvent(eventName, { logLevel: 'none' });

    const evt = flog.seq(`${operationType} ${operationName}`, {
      graphql: {
        operation: {
          name: operationName,
          type: operationType,
        },
      },
    })('START');

    // Gets a stack trace of the current call, minus node_modules lines (for clarity)
    const callStackTrace = new Error().stack
      ?.replace(/^.*[\\/]node_modules[\\/].*$/gm, '')
      .replace(/\n+/g, '\n');

    operation.setContext({
      headers: { ...operation.getContext().headers, ...transaction.headers() },
    });
    transaction.addContext({ query });

    if (CONFIG.thirdParty?.telemetryOptions?.traceGraphqlVariables) {
      try {
        transaction.addContext({
          variables: redactVariables(operationName, operation.variables),
        });
      } catch (err) {
        reportError(err);
      }
    }

    return new Observable<FetchResult>((originalObserver) => {
      let first = true;
      const subscription = forward(operation).subscribe({
        next: (result) => {
          if (result && result.errors && result.errors.length > 0) {
            const responseHeaders = operation?.getContext()?.response?.headers;
            const previewEnvironment =
              responseHeaders?.get('x-application-env');

            const graphqlErrors = new ApolloError({
              graphQLErrors: result.errors,
            });

            transaction.failed({ graphqlErrors });
            evt('FAIL', {
              graphql: { errors: graphqlErrors },
              status: 'error',
            });

            result.errors
              .filter((err) => err)
              .forEach((err) => {
                const reportedError = new GraphqlQueryError(
                  eventName,
                  err.message,
                  previewEnvironment
                );
                reportedError.stack = callStackTrace;

                reportError(reportedError);
              });
          } else {
            transaction.completed();
            if (first) {
              evt('DATA');
              first = false;
            } else {
              evt('NEXT');
            }
          }

          originalObserver.next(result);
        },

        complete: () => {
          originalObserver.complete();
        },

        error: (error) => {
          evt('FAIL', { error, status: 'error' });

          if (isServerError(error)) {
            const { result: __result, response: __response, ...rest } = error;
            // eslint-disable-next-line no-param-reassign
            error.name = `${eventName}: Network Error`;

            transaction.addContext({ error: rest });
          } else {
            const responseHeaders = operation?.getContext()?.response?.headers;
            const previewEnvironment =
              responseHeaders?.get('x-application-env');

            // eslint-disable-next-line no-param-reassign
            error.name = `${eventName}: ${error.name}`;
            // eslint-disable-next-line no-param-reassign
            error.previewEnvironment = previewEnvironment;
            transaction.addContext({ error });
          }

          reportError(error);

          transaction.failed();
          originalObserver.error(error);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    });
  });
};

function isServerError(error: unknown) {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    'result' in error &&
    'statusCode' in error
  );
}

class GraphqlQueryError extends Error {
  previewEnvironment: string;

  constructor(eventName: string, message: string, previewEnvironment: string) {
    super(`${eventName}: ${message}`);
    this.name = `GraphqlQueryError`;
    this.previewEnvironment = previewEnvironment;

    // 👇️ because we are extending a built-in class
    Object.setPrototypeOf(this, GraphqlQueryError.prototype);
  }
}

const redactVariables = (
  operationName: string,
  value: any,
  path: Array<string> = []
) => {
  let redacted: any = null;
  const dotPath = path.join('.');
  const operationPaths =
    CONFIG.thirdParty.telemetryOptions.traceGraphqlVariables[operationName] ||
    [];
  const shouldRecord = operationPaths.includes(dotPath);
  if (shouldRecord) {
    redacted = value;
  } else if (value === null) {
    redacted = null;
  } else if (Array.isArray(value)) {
    redacted = value
      .map((v) => redactVariables(operationName, v, path))
      .filter((v) => v);

    if (redacted.length === 0) redacted = null;
  } else if (isPlainObject(value)) {
    redacted = {};
    Object.keys(value).forEach((prop) => {
      const redactedPropValue = redactVariables(operationName, value[prop], [
        ...path,
        prop,
      ]);
      if (redactedPropValue) redacted[prop] = redactedPropValue;
    });

    if (Object.keys(redacted).length === 0) redacted = null;
  }

  return redacted;
};
